// IMPORT/LIBRERIE
import { useState, useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import type { RouteRecord } from "vite-react-ssg";

// CONFIGURAZIONI/COSTANTI
import { RECAPTCHA_SITE_KEY, AGENCY_ID, IUBENDA_SITE_ID, DEFAULT_AGENCY_NAME, appConfig } from "./config/appConfig";
import i18n from "./config/i18n/i18n";

// COMPONENTI
import NotFound from "./components/Error/NotFound";
import HomePage from "./components/Home/Home";
import AboutUs from "./components/AboutUs/AboutUs";
import Contacts from "./components/Contacts/Contacts";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import Immobili from "./components/Properties/Immobili";
import ImmobiliSuccess from "./components/Properties/ImmobiliSuccess";
import PropertySold from "./components/Property/PropertySold";
import Sell from "./components/Static/Vendi";
import Blog from "./components/Blog/Blog";
import BlogDetail from "./components/Blog/BlogDetail";
import Dati from "./components/Policies/Routes/Dati";
import PropertyDetail from "./components/Property/Property";
import IubendaButton from "./components/Iubenda/IubendaButton";
import ErrorBoundary from "./components/Error/ErrorBoundary";
import Loader from "./components/Loaders/Loader";
import LanguageSelector from "./components/Language/LanguageSelector";
import LanguageRouteHandler from "./components/Language/LanguageRouteHandler";
import RecaptchaWrapper from "./components/Wrappers/RecaptchaWrapper";
import PolicyPageRoute from './components/Policies/PolicyPageRoute';
import { getPolicyIdForLang } from "./utils/getPolicyIdForLang";
import WhatsAppButton from "./components/Widgets/WhatsappWidget";
import HolidayPopup from "./components/Popups/HolidayPopup";
import Services from "./components/Services/Services";
import InternalArchitect from "./components/InternalArchitect.tsx/internalArchitect";
//import Partners from "./components/Partners/Partners";
//import SellHome from "./components/VendereCasa/VendereCasa";
import Buy from "./components/Static/Cerca";

// HOOK
import { useGetAgencyDetails, Agency } from "./hooks/useGetAgencyDetails";

// FOGLI DI STILE
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import '@flaticon/flaticon-uicons/css/all/all.css';
import './index.css'
import ScrollToTop from "./utils/scrollToTop";
import TrailingSlashNormalizer from "./utils/trailingSlashNormalizer";
import PropertyPromotion from "./components/PropertryPromotion/PropertyPromotion";

interface AppShellProps {
    agencyData: Agency | null;
    children: React.ReactNode;
}

const maintenance = false;

const AppShell: React.FC<AppShellProps> = ({ agencyData, children }) => {
    const { i18n: i18nInstance } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLanguageChange = (lang: string) => {
        i18nInstance.changeLanguage(lang).then(() => {
            if (appConfig.integrations.multilang) {
                const currentPath = location.pathname;
                const newPath = currentPath.replace(/^\/[a-z]{2}(?=\/|$)/, `/${lang}`);
                if (!/^\/[a-z]{2}/.test(currentPath)) navigate(`/${lang}${currentPath}`);
                else navigate(newPath, { replace: true });
            }
        });
    };

    const headerAgencyData = {
        agency_name: agencyData?.name ?? appConfig.agency.defaults.name,
        phone1: agencyData?.phone1 ?? appConfig.agency.defaults.phone1,
        mobile: agencyData?.mobile ?? appConfig.agency.defaults.mobile,
        mobile2: agencyData?.mobile2 ?? "",
        mobile_visible: true,
        international_prefix: appConfig.agency.defaults.intlPrefix,
        whatsapp: true,
        facebook: true,
        instagram: true,
        twitter: false,
        linkedin: false,
        pinterest: false,
        cookie_solution_iubenda: true,
    };

    const currentAppLanguage = i18nInstance.language.split('-')[0];
    const buttonPolicyId = getPolicyIdForLang(currentAppLanguage);

    if (maintenance) {
        return <div className="App"><div className="maintenance-wrapper"><h1>Sito in costruzione</h1><p>Il sito è attualmente in stato di <strong>realizzazione</strong>.<br /> Torna presto a visitarci!</p><img src="assets/images/logo.png" alt="Logo" /></div></div>;
    }

    return (
        <div className="App">
            <Header
                agencyData={headerAgencyData}
                languageSelector={appConfig.ui.header.showLanguageSelector ? <LanguageSelector handleLanguageChange={handleLanguageChange} /> : <></>}
            />
            {children}
            <IubendaButton siteId={IUBENDA_SITE_ID} cookiePolicyId={buttonPolicyId} showBanner={appConfig.integrations.iubenda.showBanner} />
            <Footer />
            {appConfig.ui.showWhatsAppButton && <WhatsAppButton />}
            {appConfig.features.holidayPopup.enabled && <HolidayPopup />}
        </div>
    );
};

function App() {
    const [showAgents] = useState(true);
    const langPrefix = appConfig.integrations.multilang ? "/:lng" : "";

    if (!RECAPTCHA_SITE_KEY) {
        console.error("App.tsx: RECAPTCHA_SITE_KEY non è definito!");
        return <div>Errore di configurazione: reCAPTCHA non disponibile.</div>;
    }

    return (
        <Routes>
            <Route path={`${langPrefix}/`} element={<RecaptchaWrapper><HomePage seo={true} vetrina2={appConfig.ui.homePage.vetrina2} pageType={appConfig.ui.homePage.pageType} filters={appConfig.ui.homePage.filters} showLocationSearch={appConfig.ui.homePage.showLocationSearch} showFooterBannerVendi={appConfig.ui.homePage.showFooterBannerVendi} showPresentationSection={appConfig.ui.homePage.showPresentationSection} showSocialSection={appConfig.ui.homePage.showSocialSection} showVetrina1={appConfig.ui.homePage.showVetrina1} /></RecaptchaWrapper>} />
            <Route path={`${langPrefix}/immobili/*`} element={<Immobili />} />
            <Route path={`${langPrefix}/immobili`} element={<Immobili />} />
            <Route path={`${langPrefix}/immobile/:idSlug`} element={<RecaptchaWrapper><PropertyDetail /></RecaptchaWrapper>} />
            <Route path={`${langPrefix}/chi-siamo`} element={<AboutUs agencyId={AGENCY_ID} />} />
            <Route path={`${langPrefix}/servizi`} element={<Services />} />
            <Route path={`${langPrefix}/architetto-interno`} element={<InternalArchitect />} />
            <Route path={`${langPrefix}/promozione-immobile`} element={<PropertyPromotion />} />
            <Route path={`${langPrefix}/contatti`} element={<RecaptchaWrapper><Contacts contactForm={true} agencyId={AGENCY_ID} showAgents={showAgents} /></RecaptchaWrapper>} />
            <Route path={`${langPrefix}/dati`} element={<Dati agencyId={AGENCY_ID} />} />
            <Route path={`${langPrefix}/privacy`} element={<PolicyPageRoute policyType="privacy" />} />
            <Route path={`${langPrefix}/cookies`} element={<PolicyPageRoute policyType="cookies" />} />
            <Route path={`${langPrefix}/cerca`} element={<RecaptchaWrapper><Buy /></RecaptchaWrapper>} />
            <Route path={`${langPrefix}/vendi`} element={<RecaptchaWrapper><Sell /></RecaptchaWrapper>} />
            {appConfig.ui.routes.showBlogRoutes && <>
                <Route path={`${langPrefix}/blog`} element={<Blog agencyId={AGENCY_ID} />} />
                <Route path={`${langPrefix}/blog/:articleId`} element={<BlogDetail agencyId={AGENCY_ID} />} />
            </>}
            {appConfig.ui.routes.showSuccessRoutes && <>
                <Route path={`${langPrefix}/immobili-successi`} element={<ImmobiliSuccess />} />
                <Route path={`${langPrefix}/immobili-successi/:idSlug`} element={<PropertySold />} />
            </>}
            <Route path="*" element={<NotFound message="" />} />
        </Routes>
    );
}

interface SitePageProps {
    children?: React.ReactNode;
}

function SitePage({ children }: SitePageProps) {
    const { agency, loading, error } = useGetAgencyDetails(AGENCY_ID);
    const [timedOut, setTimedOut] = useState(false);
    const [showLoader, setShowLoader] = useState(true);

    useEffect(() => {
        if (error) console.error("Errore caricamento dati agenzia:", error);
    }, [error]);

    useEffect(() => {
        const timeout = setTimeout(() => setTimedOut(true), 4000);
        const loaderTimer = setTimeout(() => setShowLoader(false), 3000);
        return () => {
            clearTimeout(timeout);
            clearTimeout(loaderTimer);
        };
    }, []);

    const agencyReady = !loading || timedOut;
    const loaderAgencyData = { agency_name: agency?.name ?? DEFAULT_AGENCY_NAME };

    return (
        <I18nextProvider i18n={i18n}>
            <ScrollToTop />
            <TrailingSlashNormalizer />
            {!agencyReady || showLoader ? (
                <Loader agencyData={loaderAgencyData} isLoading={true} />
            ) : (
                <ErrorBoundary>
                    <LanguageRouteHandler>
                        <AppShell agencyData={agency}>{children ?? <App />}</AppShell>
                    </LanguageRouteHandler>
                </ErrorBoundary>
            )}
        </I18nextProvider>
    );
}

function AppWithI18n() {
    return <SitePage />;
}

function BlogPageWithI18n() {
    return <SitePage><Blog agencyId={AGENCY_ID} /></SitePage>;
}

function BlogDetailWithI18n() {
    return <SitePage><BlogDetail agencyId={AGENCY_ID} /></SitePage>;
}

function PropertyDetailWithI18n() {
    return <SitePage><RecaptchaWrapper><PropertyDetail /></RecaptchaWrapper></SitePage>;
}

interface PropertySsgData {
    propertyId?: number;
    typologyDetails?: { seoString?: string };
    contractDetails?: { contractName?: string };
    indirizzo?: {
        comuneSeoString?: string;
        frazioneSeoString?: string;
        quartiereSeoString?: string;
    };
}

const makePropertySlug = (property: PropertySsgData): string => {
    const propertyType = property.typologyDetails?.seoString ?? "";
    const contract = property.contractDetails?.contractName?.toLowerCase() ?? "dettaglio";
    const city = property.indirizzo?.comuneSeoString ?? "";
    const location = property.indirizzo?.frazioneSeoString ?? property.indirizzo?.quartiereSeoString ?? "";
    return `${property.propertyId}-${propertyType}-${contract}-${city}${location ? `-${location}` : ""}`;
};

export const fetchBlogArticleIds = async (): Promise<string[]> => {
    const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL as string | undefined;
    if (!apiBaseUrl) return [];

    try {
        const response = await fetch(`${apiBaseUrl}/blog/articoli`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ agencyId: AGENCY_ID, limit: 1000, withCount: false }),
        });
        if (!response.ok) return [];
        const payload = await response.json();
        const articles = Array.isArray(payload?.data) ? payload.data : [];
        return articles
            .map((article: { id?: number }) => typeof article?.id === "number" ? String(article.id) : null)
            .filter((id: string | null): id is string => Boolean(id));
    } catch (error) {
        console.warn("SSG: impossibile recuperare gli ID degli articoli.", error);
        return [];
    }
};

export const fetchPropertySlugs = async (): Promise<string[]> => {
    const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL as string | undefined;
    if (!apiBaseUrl) return [];

    try {
        const listResponse = await fetch(`${apiBaseUrl}/properties`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                agencyId: AGENCY_ID,
                limit: 10000,
                offset: 0,
                withCount: false,
                status: [2, 3, 9, 11],
            }),
        });
        if (!listResponse.ok) throw new Error(`Richiesta immobili fallita (${listResponse.status})`);

        const payload = await listResponse.json();
        const properties = Array.isArray(payload?.data) ? payload.data : [];
        const paths = new Set<string>();

        for (const item of properties) {
            const propertyId = item?.id;
            if (typeof propertyId !== "number") continue;
            paths.add(String(propertyId));

            try {
                const detailResponse = await fetch(`${apiBaseUrl}/property`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ propertyId, agencyId: AGENCY_ID, limit: 1, offset: 0 }),
                });
                if (!detailResponse.ok) continue;
                const property = await detailResponse.json() as PropertySsgData;
                if (property.propertyId) paths.add(makePropertySlug(property));
            } catch (error) {
                console.warn(`SSG: dettaglio immobile ${propertyId} non disponibile.`, error);
            }
        }

        return Array.from(paths);
    } catch (error) {
        console.warn("SSG: errore durante il fetch degli immobili.", error);
        return [];
    }
    
};

export const routes: RouteRecord[] = [
    { path: "/", Component: AppWithI18n },
    {
        path: "/immobile/:idSlug",
        Component: PropertyDetailWithI18n,
        getStaticPaths: async () => (await fetchPropertySlugs()).map((slug) => `immobile/${slug}`),
    },
    ...(appConfig.ui.routes.showBlogRoutes ? [
        { path: "/blog", Component: BlogPageWithI18n },
        {
            path: "/blog/:articleId",
            Component: BlogDetailWithI18n,
            getStaticPaths: async () => (await fetchBlogArticleIds()).map((id) => `blog/${id}`),
        },
    ] : []),
    { path: "*", Component: AppWithI18n },
];

export default AppWithI18n;